/**
 * crm/SettingsTab.jsx — Phase 75 refactor
 * Extracted from CRMShell.jsx (Phase 73 + 74). Edit lead sources / lost
 * reasons / default owners + manage per-user CRM permissions + public intake
 * tokens.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Plus, X, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import usePermissions from "@/lib/usePermissions";
import { Loading } from "./_shared";
import { IntakeTokensCard } from "./IntakeTokensCard";
import { HubspotSyncCard } from "./HubspotSyncCard";

export function SettingsTab() {
  const { user, adminMode } = useAuth();
  const can = usePermissions();
  const isAdmin = can("crm_admin") && adminMode;
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: payload } = await api.get("/crm/settings");
      setData(payload);
      setDraft({
        lead_sources: [...(payload.settings.lead_sources || [])],
        lost_reasons: [...(payload.settings.lost_reasons || [])],
        default_owner_by_stage: { ...(payload.settings.default_owner_by_stage || {}) },
      });
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await api.put("/crm/settings", draft);
      toast.success("Settings saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const toggleUserPerm = async (uid, key, val) => {
    try {
      await api.put("/crm/settings", { team_access_overrides: [{ user_id: uid, [key]: val }] });
      setData((d) => ({
        ...d,
        users: d.users.map((u) => u.id === uid ? { ...u, permissions: { ...(u.permissions || {}), [key]: val } } : u),
      }));
      toast.success("Updated");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data || !draft) return <Loading />;

  return (
    <div className="space-y-6" data-testid="crm-settings-tab">
      {!isAdmin && (
        <div className="card-flat p-3 text-xs text-amber-700 bg-amber-50 border-amber-200">
          You&apos;re viewing CRM settings in read-only mode. Switch on Admin mode to edit.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <EditableList
          label="Lead sources"
          hint="Used as the dropdown options on lead source fields."
          items={draft.lead_sources}
          onChange={(items) => setDraft((d) => ({ ...d, lead_sources: items }))}
          disabled={!isAdmin}
          testIdPrefix="crm-settings-lead-sources"
        />
        <EditableList
          label="Lost reasons"
          hint="Shown in the Mark-as-Lost modal on Deals."
          items={draft.lost_reasons}
          onChange={(items) => setDraft((d) => ({ ...d, lost_reasons: items }))}
          disabled={!isAdmin}
          testIdPrefix="crm-settings-lost-reasons"
        />
      </div>

      <div className="card-flat p-4">
        <div className="font-display text-sm font-semibold text-slate-900 mb-1">Default owner per lead stage</div>
        <div className="text-xs text-slate-500 mb-3">When a lead lands in a stage and the row has no owner, this owner is suggested. (Not yet auto-assigned — informational only.)</div>
        <div className="space-y-2">
          {data.stages.map((stage) => (
            <div key={stage} className="flex items-center gap-3" data-testid={`crm-settings-stage-${stage.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="w-44 text-xs text-slate-700">{stage}</div>
              <select
                value={draft.default_owner_by_stage[stage] || ""}
                onChange={(e) => setDraft((d) => ({ ...d, default_owner_by_stage: { ...d.default_owner_by_stage, [stage]: e.target.value } }))}
                disabled={!isAdmin}
                className="flex-1 rounded-sm border border-slate-200 text-sm h-9 px-2 disabled:bg-slate-50 disabled:text-slate-400"
              >
                <option value="">— No default —</option>
                {data.users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email} ({u.role})</option>)}
              </select>
            </div>
          ))}
        </div>
      </div>

      {isAdmin && (
        <div className="flex justify-end">
          <Button size="sm" onClick={saveSettings} disabled={saving} className="rounded-sm h-9 bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="crm-settings-save">
            {saving ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Save size={12} className="mr-1.5" />} Save dictionaries + defaults
          </Button>
        </div>
      )}

      <div className="card-flat overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200">
          <div className="font-display text-sm font-semibold text-slate-900">Team CRM access</div>
          <div className="text-xs text-slate-500">Per-user toggles propagate into the canonical user permissions map. Admins can grant or revoke CRM access individually here.</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="crm-settings-team-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">User</th>
                <th className="px-4 py-2 text-left">Role</th>
                <th className="px-4 py-2 text-center">CRM access</th>
                <th className="px-4 py-2 text-center">CRM edit</th>
                <th className="px-4 py-2 text-center">CRM admin</th>
              </tr>
            </thead>
            <tbody>
              {data.users.map((u) => (
                <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`crm-settings-user-row-${u.id}`}>
                  <td className="px-4 py-2">
                    <div className="text-slate-900 font-medium">{u.name || u.email}</div>
                    <div className="text-[10px] text-slate-500">{u.email}</div>
                  </td>
                  <td className="px-4 py-2"><span className="text-[10px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded-sm uppercase tracking-wider">{u.role}</span></td>
                  {["crm_access", "crm_edit", "crm_admin"].map((k) => {
                    const val = u.permissions?.[k] !== undefined ? u.permissions[k] : (u.role !== "client" && u.role !== "vendor" && k !== "crm_admin");
                    return (
                      <td key={k} className="px-4 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={!!val}
                          disabled={!isAdmin}
                          onChange={(e) => toggleUserPerm(u.id, k, e.target.checked)}
                          className="accent-[#F54800] disabled:opacity-50"
                          data-testid={`crm-settings-perm-${k}-${u.id}`}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Phase 74 — Public intake tokens */}
      <IntakeTokensCard isAdmin={isAdmin} />

      {/* Feb 2026 — HubSpot Contacts/Deals sync */}
      <HubspotSyncCard isAdmin={isAdmin} />
    </div>
  );
}

function EditableList({ label, hint, items, onChange, disabled, testIdPrefix }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v || items.includes(v)) { setDraft(""); return; }
    onChange([...items, v]);
    setDraft("");
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="card-flat p-4" data-testid={testIdPrefix}>
      <div className="font-display text-sm font-semibold text-slate-900 mb-1">{label}</div>
      <div className="text-xs text-slate-500 mb-3">{hint}</div>
      <div className="flex flex-wrap gap-1.5 mb-3 min-h-[28px]">
        {items.length === 0 && <div className="text-xs text-slate-300 italic">No entries yet.</div>}
        {items.map((it, i) => (
          <span key={i} className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-700 pl-2 pr-1 py-0.5 rounded-sm" data-testid={`${testIdPrefix}-item-${i}`}>
            {it}
            {!disabled && (
              <button onClick={() => remove(i)} className="text-slate-400 hover:text-rose-600 p-0.5" data-testid={`${testIdPrefix}-remove-${i}`}>
                <X size={10} />
              </button>
            )}
          </span>
        ))}
      </div>
      {!disabled && (
        <div className="flex gap-1.5">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
            placeholder="Add new…"
            className="rounded-sm h-8 text-sm"
            data-testid={`${testIdPrefix}-input`}
          />
          <Button size="sm" onClick={add} variant="outline" className="rounded-sm h-8 px-2" data-testid={`${testIdPrefix}-add`}>
            <Plus size={11} />
          </Button>
        </div>
      )}
    </div>
  );
}
